/**
 * PrintingSettingsCard — a impressão do ticket, no módulo Configurações (Onda 97)
 * CI Sistemas de Informática Ltda. — AcessoiD
 *
 * As chaves existem desde a Onda 80, mas nunca tiveram tela: `printing_enabled`
 * ficou desligada nos dois servidores e só se mexia nela por SQL.
 *
 * Este cartão NÃO usa o salvamento genérico da página: fala com
 * `/api/printing/config`, que valida os valores, registra a mudança na auditoria
 * e — ao LIGAR — dispensa os tickets acumulados (todo ticket nasce `pendente`
 * pelo padrão da coluna; sem a dispensa, o primeiro coletor imprimiria carro que
 * saiu há uma semana). Gravar essas chaves pelo caminho genérico pularia as três
 * coisas.
 *
 * NÃO HÁ IMPRESSÃO AUTOMÁTICA — decisão do Iatagã, 19/09/2026. O papel só sai
 * depois que o operador confere a placa no app e toca em imprimir. Por isso não
 * existe aqui um seletor "manual × automático": uma opção que ninguém deve ligar
 * é só um jeito de alguém ligá-la por engano.
 *
 * O TEXTO DO TICKET É POR LINHA. Uma térmica de 58 mm tem 32 colunas, e uma de
 * 80 mm tem 48; linha mais larga que isso quebra sozinha na impressora e
 * desalinha o ticket todo. Então o limite que a tela impõe é o do papel — por
 * linha, com contador —, e a prévia ao lado mostra o ticket como ele sai. A
 * prévia NÃO é decoração: é o que poupa o dono de gastar bobina para descobrir
 * que o CNPJ ficou cortado.
 *
 * Fora da vertical estacionamento a rota devolve 403, e o cartão some.
 */
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api';
import '../styles/PrintingSettings.css';

// Exportado para o teste da Onda 106 conferir, modo a modo, que a tela oferece
// exatamente o que o servidor aceita — divergir aqui produz um select que grava
// um valor recusado, ou que esconde um modo que existe.
export const MODOS = [
  { valor: 'coletor',   rotulo: 'Impressora do coletor',  pedeEndereco: false,
    ajuda: 'A impressora embutida no próprio aparelho (VT-Q2i e parecidos). O app a encontra sozinho — não há endereço para informar, e o Bluetooth do aparelho nem precisa estar ligado.' },
  { valor: 'mini_bluetooth', rotulo: 'Mini impressora Bluetooth (escolhida no app)', pedeEndereco: false,
    ajuda: 'Uma mini impressora térmica de bolso, pareada com o celular da guarita. Não há endereço para informar aqui: cada aparelho escolhe a sua no próprio app, em ⋮ › Impressora Bluetooth — é o que permite duas guaritas, cada uma com o seu celular e a sua impressora. O ticket sai idêntico ao do coletor.' },
  { valor: 'bluetooth', rotulo: 'Bluetooth externa (por endereço MAC)', pedeEndereco: true,  exemplo: '00:11:22:33:44:55',
    ajuda: 'Uma impressora térmica Bluetooth definida aqui, pelo endereço MAC, valendo para todos os aparelhos. Use quando houver uma impressora só e você souber o MAC; se cada guarita tem a sua, prefira a opção acima.' },
  { valor: 'rede',      rotulo: 'Rede (ESC/POS por TCP)', pedeEndereco: true,  exemplo: '192.168.15.60:9100',
    ajuda: 'Uma impressora térmica ligada na rede local da guarita. Informe o IP; a porta padrão é 9100.' },
];

const VAZIO = { habilitado: false, modo: 'coletor', endereco: '', larguraPapel: 58, cabecalho: '', rodape: '' };
const colunasDe = (largura) => (Number(largura) === 80 ? 48 : 32);

function linhasDe(texto) {
  const l = String(texto || '').replace(/\r/g, '').split('\n').map(x => x.replace(/\s+$/, ''));
  while (l.length && !l[l.length - 1]) l.pop();
  return l;
}

/** `null` se cabe; senão, o que não cabe — a mesma regra do servidor. */
function problemaNoTexto(texto, { colunas, maxLinhas, nome }) {
  const l = linhasDe(texto);
  if (l.length > maxLinhas) return `O ${nome} aceita até ${maxLinhas} linhas (há ${l.length}).`;
  const i = l.findIndex(x => x.length > colunas);
  if (i >= 0) return `A linha ${i + 1} do ${nome} tem ${l[i].length} caracteres; neste papel cabem ${colunas}.`;
  return null;
}

/** Campo de várias linhas com o contador de cada linha ao lado. */
function TextoDoTicket({ id, rotulo, ajuda, valor, aoMudar, colunas, maxLinhas, exemplo }) {
  const linhas = linhasDe(valor);
  // Sempre as `maxLinhas` do papel, mesmo com o campo vazio: o dono vê quantas
  // linhas tem para usar. Linha além do limite aparece também, marcada.
  const contadores = Array.from({ length: Math.max(maxLinhas, linhas.length) }, (_, i) => linhas[i] || '');
  return (
    <div className="settings-field">
      <label className="field-label" htmlFor={id}>{rotulo}</label>
      <p className="field-desc">{ajuda}</p>
      <div className="imp-texto" style={{ '--imp-colunas': colunas }}>
        <textarea
          id={id} className="field-input imp-textarea" rows={Math.max(maxLinhas, linhas.length)} wrap="off" spellCheck={false}
          placeholder={exemplo} value={valor}
          onChange={e => aoMudar(e.target.value)}
        />
        <ol className="imp-contadores" aria-label="Caracteres por linha">
          {contadores.map((l, i) => {
            const estourou = l.length > colunas || i >= maxLinhas;
            return <li key={i} className={estourou ? 'imp-estourou' : ''}>{i >= maxLinhas ? 'a mais' : `${l.length}/${colunas}`}</li>;
          })}
        </ol>
      </div>
    </div>
  );
}

/**
 * O ticket como ele sai. Desde a Onda 98 o app DESENHA o ticket como imagem
 * (`mobile/lib/impressao/cupom_imagem.dart`), e esta prévia espelha aquele
 * desenho: o título entre dois filetes — sem fundo preto, que o Iatagã achou
 * estranho e desnecessário —, a placa numa moldura como o maior elemento do
 * papel, respiro entre os blocos, a hora em destaque e o QR no centro. Os acentos
 * saem no papel: quem desenha a letra é o app, não a impressora.
 */
function PreviaDoTicket({ cabecalho, rodape, colunas }) {
  const cab = linhasDe(cabecalho).filter(l => l.trim());
  const nome = (cab[0] || 'AcessoiD').trim().toUpperCase();
  const rod = linhasDe(rodape).filter(l => l.trim());
  const hoje = new Date();
  const dd = (n) => String(n).padStart(2, '0');
  const data = `${dd(hoje.getDate())}/${dd(hoje.getMonth() + 1)}/${hoje.getFullYear()}`;
  const hora = `${dd(hoje.getHours())}:${dd(hoje.getMinutes())}`;
  return (
    <div className={`imp-previa ${colunas === 48 ? 'imp-previa-80' : ''}`} aria-label="Prévia do ticket impresso">
      <div className="imp-nome">{nome}</div>
      {cab.slice(1).map((l, i) => <div key={i} className="imp-linha">{l.trim()}</div>)}
      <div className="imp-titulo">TICKET DE ENTRADA</div>
      <div className="imp-placa">ABC1D23</div>
      <div className="imp-quando">
        <div><span className="imp-rotulo">ENTRADA</span><span className="imp-data">{data}</span></div>
        <div className="imp-dir"><span className="imp-rotulo">HORA</span><span className="imp-hora">{hora}</span></div>
      </div>
      <div className="imp-tracejado" />
      <div className="imp-qr" aria-hidden="true" />
      <div className="imp-codigo">K7M2 P9QX</div>
      <div className="imp-linha">ticket 17540</div>
      <div className="imp-tracejado" />
      {rod.map((l, i) => <div key={i} className="imp-linha">{l}</div>)}
      <div className="imp-assinatura">AcessoiD</div>
    </div>
  );
}

export default function PrintingSettingsCard() {
  const [cfg, setCfg] = useState(null);      // o que está gravado
  const [form, setForm] = useState(VAZIO);   // o que está na tela
  const [visivel, setVisivel] = useState(true);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    let vivo = true;
    api.get('/api/printing/config')
      .then(r => { if (vivo) { setCfg(r.data); setForm({ ...VAZIO, ...r.data }); } })
      .catch(() => { if (vivo) setVisivel(false); });   // outra vertical, ou perfil sem alcance
    return () => { vivo = false; };
  }, []);

  const colunas = colunasDe(form.larguraPapel);
  const maxCab = cfg?.maxLinhasCabecalho || 4;
  const maxRod = cfg?.maxLinhasRodape || 4;
  const problema = useMemo(() => (
    problemaNoTexto(form.cabecalho, { colunas, maxLinhas: maxCab, nome: 'cabeçalho' })
    || problemaNoTexto(form.rodape, { colunas, maxLinhas: maxRod, nome: 'rodapé' })
  ), [form.cabecalho, form.rodape, colunas, maxCab, maxRod]);

  if (!visivel || !cfg) return null;

  const modo = MODOS.find(m => m.valor === form.modo) || MODOS[0];
  const campos = ['habilitado', 'modo', 'endereco', 'larguraPapel', 'cabecalho', 'rodape'];
  const mudou = campos.some(c => String(form[c] ?? '') !== String(({ ...VAZIO, ...cfg })[c] ?? ''));
  const faltaEndereco = form.habilitado && modo.pedeEndereco && !String(form.endereco || '').trim();
  const set = (campo, valor) => setForm(f => ({ ...f, [campo]: valor }));

  const salvar = async () => {
    setSalvando(true);
    try {
      const r = await api.put('/api/printing/config', {
        printing_enabled: !!form.habilitado,
        printer_mode: form.modo,
        printer_address: modo.pedeEndereco ? String(form.endereco || '').trim() : '',
        printer_paper_width: Number(form.larguraPapel) === 80 ? 80 : 58,
        ticket_header: form.cabecalho || '',
        ticket_footer: form.rodape || '',
      });
      setCfg(r.data);
      setForm({ ...VAZIO, ...r.data });
      const ligou = !cfg.habilitado && r.data.habilitado;
      toast.success(ligou
        ? `Impressão ligada. ${r.data.dispensados || 0} ticket(s) antigos foram dispensados de papel.`
        : 'Configuração de impressão gravada.');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Não foi possível gravar a configuração de impressão');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="settings-section">
      <div className="settings-section-header">
        <span className="section-icon">🖨️</span>
        <div>
          <h3 className="section-title">Impressão do Ticket</h3>
          <p className="section-desc">
            Como o ticket de entrada vira papel na guarita. <strong>A impressão nunca é automática:</strong> a
            entrada aparece no app, o operador confere a placa com os olhos — corrige, se preciso — e toca em
            imprimir. Um ticket com a placa errada na mão do cliente custa mais do que um segundo de conferência.
          </p>
        </div>
      </div>

      <div className="settings-section-body">
        <div className="settings-field toggle-field">
          <div className="toggle-info">
            <span className="toggle-label">Impressão de ticket</span>
            <span className="toggle-desc">
              Ligada, o app da guarita passa a oferecer o botão de imprimir em cada entrada. Desligada, nada
              muda na operação. Ao ligar, os tickets que já estão no pátio são dispensados de papel — só as
              entradas novas pedem impressão.
            </span>
          </div>
          <button
            type="button"
            className={`toggle-switch ${form.habilitado ? 'on' : 'off'}`}
            onClick={() => set('habilitado', !form.habilitado)}
            aria-pressed={!!form.habilitado}
          >
            <span className="toggle-knob" />
            <span className="toggle-text">{form.habilitado ? 'ATIVADO' : 'DESATIVADO'}</span>
          </button>
        </div>

        <div className="settings-field">
          <label className="field-label" htmlFor="imp-modo">Onde o ticket é impresso</label>
          <p className="field-desc">{modo.ajuda}</p>
          <select id="imp-modo" className="field-select" value={form.modo} onChange={e => set('modo', e.target.value)}>
            {MODOS.map(m => <option key={m.valor} value={m.valor}>{m.rotulo}</option>)}
          </select>
        </div>

        {modo.pedeEndereco && (
          <div className="settings-field">
            <label className="field-label" htmlFor="imp-endereco">Endereço da impressora</label>
            <p className="field-desc">
              {form.modo === 'rede' ? 'IP ou IP:porta da impressora na rede local.' : 'Endereço MAC da impressora Bluetooth.'}
            </p>
            <input
              id="imp-endereco" className="field-input" type="text" maxLength={64}
              placeholder={modo.exemplo} value={form.endereco || ''}
              onChange={e => set('endereco', e.target.value)}
            />
            {faltaEndereco && <p className="field-desc imp-aviso">Informe o endereço para ligar a impressão neste modo.</p>}
          </div>
        )}

        <div className="settings-field">
          <label className="field-label" htmlFor="imp-papel">Largura do papel</label>
          <p className="field-desc">
            58 mm nos coletores e impressoras portáteis; 80 mm nas de balcão. É a largura que decide
            <strong> quantos caracteres cabem em cada linha</strong> do cabeçalho e do rodapé.
          </p>
          <select id="imp-papel" className="field-select" value={String(form.larguraPapel)} onChange={e => set('larguraPapel', Number(e.target.value))}>
            <option value="58">58 mm — 32 caracteres por linha</option>
            <option value="80">80 mm — 48 caracteres por linha</option>
          </select>
        </div>

        <div className="imp-editor">
          <div className="imp-campos">
            <TextoDoTicket
              id="imp-cabecalho" rotulo="Cabeçalho do ticket" valor={form.cabecalho || ''} aoMudar={v => set('cabecalho', v)}
              colunas={colunas} maxLinhas={maxCab}
              exemplo={'G2 PARKING\nAv. Central, 1200 - Centro\nCNPJ 00.000.000/0001-00\n(11) 4000-0000'}
              ajuda={`Até ${maxCab} linhas de ${colunas} caracteres. A primeira é o nome do estacionamento e sai em destaque, em letras grandes — quanto mais curto, maior. As demais saem em letra normal: endereço, CNPJ, telefone. Vazio, o ticket leva "AcessoiD".`}
            />
            <TextoDoTicket
              id="imp-rodape" rotulo="Rodapé do ticket" valor={form.rodape || ''} aoMudar={v => set('rodape', v)}
              colunas={colunas} maxLinhas={maxRod}
              exemplo={'Guarde este ticket.\nApresente-o na saída.\nTolerância de 15 minutos.'}
              ajuda={`Até ${maxRod} linhas de ${colunas} caracteres, centralizadas: avisos, tolerância, horário de funcionamento.`}
            />
            <p className="field-desc">
              Pode escrever <strong>com acentos</strong>: o app desenha o ticket como imagem, então "saída" e
              "Operária" saem no papel como você digitou.
            </p>
          </div>

          <div className="imp-lado">
            <span className="field-label">Como o ticket sai</span>
            <PreviaDoTicket cabecalho={form.cabecalho} rodape={form.rodape} colunas={colunas} />
            <p className="field-desc">
              Placa, data, hora e código são de exemplo. O <strong>QR code central</strong> leva o código do
              ticket — é ele que o identifica na saída, mesmo se a placa for corrigida depois de o papel sair.
            </p>
          </div>
        </div>

        <div className="settings-field">
          {problema && <p className="field-desc imp-aviso">{problema}</p>}
          <button type="button" className="btn-primary" onClick={salvar} disabled={salvando || !mudou || faltaEndereco || !!problema}>
            {salvando ? 'Gravando…' : 'Gravar impressão'}
          </button>
          <p className="field-desc">Para conferir no papel de verdade, use <em>Testar impressora</em> no menu do app da guarita.</p>
        </div>
      </div>
    </div>
  );
}
